/**
 * Every number, date and duration this product shows, in the reader's language.
 *
 * A pure module with no React, so the formatters are callable from tests, from the pure
 * presentation modules, and from anywhere else that has a locale (PRD-27 LOC-020). The
 * provider re-exports locale-bound versions so call sites stay readable.
 *
 * ── Why the instances are cached ──
 *
 * `new Intl.NumberFormat(...)` is expensive — it resolves a locale, loads the data and
 * compiles a pattern. Activity, Knowledge and the conversation list all render hundreds of
 * formatted values per pass and re-render on a poll, so constructing per call turned into
 * real frame time. Cached per `(locale, options)`, which is why options are stringified
 * into the key rather than compared.
 *
 * ── What deliberately does not come from Intl ──
 *
 * Three unit suffixes stay hand-written, and the decision is per-unit rather than blanket:
 *
 *   - `ms` / `s` / `m` in `formatDuration` and `k` in `formatTokens` are a design spec, not
 *     prose. `Intl` compact notation would render "1.2K" in English and "1,2 mil" in
 *     Portuguese, and "mil" in a four-character telemetry chip is not an improvement. The
 *     NUMBER still goes through `Intl`, so the decimal separator localizes: `1.2s` · `1,2s`.
 *   - Byte units (B/KB/MB/GB/TB) are binary and match how every quota in this product is
 *     written (`262_144_000` is 250 MiB). `Intl`'s `unit: 'megabyte'` is decimal and would
 *     silently disagree with the limit the server enforces.
 *
 * Currency is NOT such a case and does go through `Intl`: "US$ 1,23" is what a Brazilian
 * reader expects, and the leading `$` of a bare dollar sign is ambiguous against the real.
 */

import type { Locale } from './locale'

const dateTimeCache = new Map<string, Intl.DateTimeFormat>()
const numberCache = new Map<string, Intl.NumberFormat>()
const relativeCache = new Map<string, Intl.RelativeTimeFormat>()
const listCache = new Map<string, Intl.ListFormat>()
const collatorCache = new Map<string, Intl.Collator>()
const pluralCache = new Map<string, Intl.PluralRules>()

function dateTimeFormatter(locale: Locale, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let formatter = dateTimeCache.get(key)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale, options)
    dateTimeCache.set(key, formatter)
  }
  return formatter
}

function numberFormatter(locale: Locale, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = `${locale}|${JSON.stringify(options)}`
  let formatter = numberCache.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, options)
    numberCache.set(key, formatter)
  }
  return formatter
}

/**
 * `numeric: 'always'` on purpose. With `'auto'`, Portuguese renders -2 days as "anteontem"
 * and 0 minutes as "este minuto" — correct Portuguese for a date, wrong for the elapsed
 * duration these call sites mean, and inconsistent beside "há 3 h". `'narrow'` is what makes
 * English read "5m ago" and "in 3d", which is byte-identical to the hand-rolled formatter
 * this replaced — so no English rendering moved when it landed.
 */
function relativeFormatter(locale: Locale): Intl.RelativeTimeFormat {
  let formatter = relativeCache.get(locale)
  if (!formatter) {
    formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'always', style: 'narrow' })
    relativeCache.set(locale, formatter)
  }
  return formatter
}

function listFormatter(locale: Locale, type: 'conjunction' | 'disjunction'): Intl.ListFormat {
  const key = `${locale}|${type}`
  let formatter = listCache.get(key)
  if (!formatter) {
    formatter = new Intl.ListFormat(locale, { style: 'long', type })
    listCache.set(key, formatter)
  }
  return formatter
}

export function collator(locale: Locale): Intl.Collator {
  let instance = collatorCache.get(locale)
  if (!instance) {
    // `base` so "servico" matches "Serviço" and "Sao" matches "São"; `numeric` so "Base 10"
    // sorts after "Base 9" rather than before it (LOC-023).
    instance = new Intl.Collator(locale, { sensitivity: 'base', numeric: true })
    collatorCache.set(locale, instance)
  }
  return instance
}

/**
 * The plural category for a count, per CLDR.
 *
 * Both shipped locales have two categories, which is exactly why this exists rather than an
 * `n === 1` check: Brazilian Portuguese puts ZERO in the `one` category ("0 documento",
 * not "0 documentos") while English puts it in `other` ("0 documents"). A hand-rolled
 * comparison is wrong on every zero state in the product, in one language only, silently.
 */
export function pluralCategory(locale: Locale, count: number): Intl.LDMLPluralRule {
  let rules = pluralCache.get(locale)
  if (!rules) {
    rules = new Intl.PluralRules(locale)
    pluralCache.set(locale, rules)
  }
  return rules.select(count)
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value)
}

// ---------------------------------------------------------------------------
// Dates and times
// ---------------------------------------------------------------------------

/** en: "Sep 20, 2026" · pt-BR: "20 de set. de 2026" — note the day comes first. */
export function formatDate(locale: Locale, value: Date | string | number): string {
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return '—'
  return dateTimeFormatter(locale, { year: 'numeric', month: 'short', day: 'numeric' }).format(date)
}

/** A date without its year, for anything inside the current one. en: "Sep 20" · pt-BR: "20 de set." */
export function formatDayMonth(locale: Locale, value: Date | string | number): string {
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return '—'
  return dateTimeFormatter(locale, { month: 'short', day: 'numeric' }).format(date)
}

/**
 * The weekday alone, for a recency column that shows a day name inside the last week.
 * en "Sun" · pt-BR "dom." — and never a hand-written array, which is wrong in every language
 * including the one it was written for the moment a locale needs a different abbreviation.
 */
export function formatWeekday(locale: Locale, value: Date | string | number): string {
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return '—'
  return dateTimeFormatter(locale, { weekday: 'short' }).format(date)
}

/** en: "Sep 20 at 2:03 PM" · pt-BR: "20 de set., 14:03" — the separator is the locale's, not ours. */
export function formatDateTime(locale: Locale, value: Date | string | number): string {
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return '—'
  return dateTimeFormatter(locale, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date)
}

/** en: "2:03 PM" · pt-BR: "14:03". `hour: 'numeric'` so English is not "02:03 PM". */
export function formatTime(locale: Locale, value: Date | string | number): string {
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return '—'
  return dateTimeFormatter(locale, { hour: 'numeric', minute: '2-digit' }).format(date)
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

export function formatNumber(locale: Locale, value: number, options?: Intl.NumberFormatOptions): string {
  if (!Number.isFinite(value)) return '—'
  return numberFormatter(locale, options ?? {}).format(value)
}

/** A whole count with the locale's grouping: en "1,234" · pt-BR "1.234". */
export function formatCount(locale: Locale, value: number): string {
  return formatNumber(locale, value, { maximumFractionDigits: 0 })
}

/** Takes a FRACTION, not a percentage: 0.427 → en "42.7%" · pt-BR "42,7%". */
export function formatPercent(locale: Locale, fraction: number, maximumFractionDigits = 1): string {
  if (!Number.isFinite(fraction)) return '—'
  return numberFormatter(locale, { style: 'percent', maximumFractionDigits }).format(fraction)
}

/**
 * Cost in USD, because that is what the model providers bill in — only the format is
 * localized, never the currency (PRD-27 non-goals).
 *
 * Sub-cent costs are the norm for a single turn, so rounding them to two decimals would
 * render "$0.00" for every message and make the readout look broken.
 */
export function formatCost(locale: Locale, usd: number): string {
  if (!Number.isFinite(usd)) return '—'
  const digits = usd === 0 ? 0 : usd < 0.01 ? 4 : 2
  return numberFormatter(locale, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  }).format(usd)
}

/** en "1.2k" · pt-BR "1,2k". The `k` is a spec unit; the number is the locale's. */
export function formatTokens(locale: Locale, count: number): string {
  if (!Number.isFinite(count)) return '—'
  if (count < 1000) return formatCount(locale, count)
  const digits = count >= 10_000 ? 0 : 1
  return `${formatNumber(locale, count / 1000, { minimumFractionDigits: digits, maximumFractionDigits: digits })}k`
}

/**
 * Binary units, matching every quota the server enforces. en "1.5 MB" · pt-BR "1,5 MB".
 *
 * ── Why a round size is round ──
 *
 * This function folded TWO formatters that disagreed. `primitives.formatBytes` used
 * `.toFixed(n)` and always printed the trailing zero ("1.0 GB"); `storage-presentation`
 * `formatSize` deliberately trimmed it, with its reason written down: *a trailing ".0" is
 * noise rather than precision — a 250 MB quota should read "250 MB", the way the same limit
 * is written everywhere else in the product*. Folding them into one implementation (LOC-020)
 * means one of the two has to move, and the argued rule wins: a quota is a round number and
 * should read like one. So "1.0 GB" is now "1 GB" on the knowledge screens, which is a
 * deliberate English change recorded as PRD-27 D-30 rather than an accident of the refactor.
 *
 * `maximumFractionDigits` alone is what produces the trim, since `Intl` drops trailing zeros
 * unless a minimum is set. `formatDuration` and `formatTokens` do the opposite and set both,
 * because THEY had no competing rule to weigh and a precision readout in a fixed-width column
 * is better off keeping its digit.
 */
export function formatBytes(locale: Locale, bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${formatCount(locale, bytes)} B`
  if (bytes < 1024 ** 2) return `${formatNumber(locale, bytes / 1024, { maximumFractionDigits: 0 })} KB`
  if (bytes < 1024 ** 3) return `${formatNumber(locale, bytes / 1024 ** 2, { maximumFractionDigits: 1 })} MB`
  if (bytes < 1024 ** 4) return `${formatNumber(locale, bytes / 1024 ** 3, { maximumFractionDigits: 1 })} GB`
  return `${formatNumber(locale, bytes / 1024 ** 4, { maximumFractionDigits: 2 })} TB`
}

/**
 * `toFixed` semantics with the locale's separators: exactly `digits` decimals, trailing zeros
 * kept. The localized counterpart of the `.toFixed(n)` this module replaced.
 */
function fixed(locale: Locale, value: number, digits: number): string {
  return formatNumber(locale, value, { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

/**
 * en "850ms" · "1.2s" · "2.0s" · "3m 30s" — pt-BR the same shape with "1,2s".
 *
 * The seconds tier keeps its trailing zero for the same reason `formatBytes` does: an exact
 * two seconds has always rendered "2.0s", and no spec pinned it, so this one changed silently
 * on the latency cards, the trace nodes and the budget readouts before it was caught.
 */
export function formatDuration(locale: Locale, ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—'
  if (ms < 1000) return `${Math.round(ms)}ms`
  if (ms < 60_000) return `${fixed(locale, ms / 1000, 1)}s`
  return `${formatCount(locale, Math.floor(ms / 60_000))}m ${formatCount(locale, Math.round((ms % 60_000) / 1000))}s`
}

/**
 * Seconds, always, whatever the magnitude — "0.8s", "3.4s", "185.0s".
 *
 * The counterpart to `formatDuration`, and a separate function rather than an option because
 * the two answer different questions. `formatDuration` picks the unit that reads best for ONE
 * value; this one refuses to, because its callers render a COLUMN that a person compares by
 * eye, and a list that re-units a fast read to "850ms" and a slow one to "3m 5s" cannot be
 * scanned at all. Grouping is off for the same reason: a fixed-width readout should not gain
 * a separator at a thousand seconds.
 */
export function formatSeconds(locale: Locale, ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—'
  return `${formatNumber(locale, ms / 1000, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    useGrouping: false
  })}s`
}

// ---------------------------------------------------------------------------
// Lists
// ---------------------------------------------------------------------------

/**
 * The one list join in this repository (LOC-016). It replaced three `joinNames`
 * implementations and five `people()` copies, every one of which hardcoded " and ".
 * en "Ana, Rui, and Léo" · pt-BR "Ana, Rui e Léo" — note Portuguese has no serial comma.
 */
export function formatList(locale: Locale, items: readonly string[]): string {
  return listFormatter(locale, 'conjunction').format(items as string[])
}

/** "Ana or Rui" · "Ana ou Rui" — for a choice rather than a set. */
export function formatListOr(locale: Locale, items: readonly string[]): string {
  return listFormatter(locale, 'disjunction').format(items as string[])
}

// ---------------------------------------------------------------------------
// Relative time
// ---------------------------------------------------------------------------

/**
 * How long ago. en "5m ago" · pt-BR "há 5 min.".
 *
 * `justNow` and `absoluteAfterDays` are parameters rather than catalogue reads because this
 * module stays pure and React-free; the provider binds them from `t.common`. The English
 * defaults are the source-language catalogue's own words, which is what lets every existing
 * call site and its test keep working untouched while the localized callers pass the active
 * pack.
 *
 * Beyond a week a relative duration stops being informative ("53d ago" tells nobody
 * anything), so it becomes an absolute date — which is also why this cannot be
 * `Intl.RelativeTimeFormat` alone.
 */
export function formatAgo(
  locale: Locale,
  value: Date | string | number,
  options: { nowMs?: number; justNow?: string } = {}
): string {
  const then = toDate(value).getTime()
  if (Number.isNaN(then)) return '—'
  const nowMs = options.nowMs ?? Date.now()
  const diff = nowMs - then
  if (diff < 60_000) return options.justNow ?? 'just now'
  const rtf = relativeFormatter(locale)
  if (diff < 3_600_000) return rtf.format(-Math.floor(diff / 60_000), 'minute')
  if (diff < 86_400_000) return rtf.format(-Math.floor(diff / 3_600_000), 'hour')
  if (diff < 7 * 86_400_000) return rtf.format(-Math.floor(diff / 86_400_000), 'day')
  return formatDayMonth(locale, then)
}

/**
 * The forward-looking twin, and a separate function rather than a sign branch inside
 * `formatAgo` for the reason the original recorded: clamping a non-positive delta to "just
 * now" is right for a timestamp and wrong for a deadline — it made every pending approval in
 * the governance queue read "expires just now", i.e. every one of them looked about to
 * lapse. The two have different vocabularies and a caller has to choose.
 */
export function formatUntil(
  locale: Locale,
  value: Date | string | number,
  options: { nowMs?: number; expired?: string; underAMinute?: string } = {}
): string {
  const target = toDate(value).getTime()
  if (Number.isNaN(target)) return '—'
  const delta = target - (options.nowMs ?? Date.now())
  if (delta <= 0) return options.expired ?? 'expired'
  if (delta < 60_000) return options.underAMinute ?? 'in under a minute'
  const rtf = relativeFormatter(locale)
  if (delta < 3_600_000) return rtf.format(Math.floor(delta / 60_000), 'minute')
  if (delta < 86_400_000) return rtf.format(Math.floor(delta / 3_600_000), 'hour')
  return rtf.format(Math.floor(delta / 86_400_000), 'day')
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

/**
 * Strip diacritics for substring matching (LOC-023).
 *
 * `Intl.Collator` with `sensitivity: 'base'` settles COMPARISON and SORTING, but it cannot
 * answer "does this string contain that one", which is what the command palette and every
 * filter box actually ask. A Brazilian habitually types "servico" for "Serviço" and
 * "conexao" for "conexão"; before this, both returned nothing at all.
 */
export function foldForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
}
